import type { IPlatformAdapter } from '../ports/IPlatformAdapter'
import type {
  BackButtonOptions,
  HapticStyle,
  MainButtonOptions,
  PlatformLaunchPayload,
  PlatformTheme,
} from '../ports/types'
import type {
  TelegramMainButton,
  TelegramWebApp,
} from '../types/telegram-web-app'
import { openExternalUrl } from '../openExternalUrl'

/** Telegram may never fire `contactRequested` after `shared === true` — settle anyway. */
const REQUEST_CONTACT_TIMEOUT_MS = 10_000

/**
 * Telegram Mini App adapter over official `telegram-web-app.js` (`window.Telegram.WebApp`).
 * Script is injected from the Platform Nuxt layer head.
 */
export class TelegramPlatformAdapter implements IPlatformAdapter {
  readonly kind = 'telegram' as const

  private mainClick: (() => void) | null = null
  private backClick: (() => void) | null = null

  private get webApp(): TelegramWebApp | undefined {
    return typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined
  }

  async init(): Promise<PlatformLaunchPayload> {
    const webApp = this.webApp
    if (!webApp) {
      return { kind: this.kind, raw: null, startParam: null }
    }

    webApp.ready()
    webApp.expand()

    return {
      kind: this.kind,
      raw: webApp.initData || null,
      startParam: this.getStartParam(),
    }
  }

  getTheme(): PlatformTheme {
    const webApp = this.webApp
    if (!webApp) {
      return { colorScheme: 'light' }
    }
    const tp = webApp.themeParams || {}
    return {
      colorScheme: webApp.colorScheme === 'dark' ? 'dark' : 'light',
      bgColor: tp.bg_color,
      textColor: tp.text_color,
      hintColor: tp.hint_color,
      linkColor: tp.link_color,
      buttonColor: tp.button_color,
      buttonTextColor: tp.button_text_color,
      secondaryBgColor: tp.secondary_bg_color,
    }
  }

  setMainButton(options: MainButtonOptions | null): void {
    const button = this.webApp?.MainButton
    if (!button) {
      return
    }

    this.clearMainClick(button)

    if (!options || options.visible === false) {
      button.hide()
      return
    }

    this.applyMainButtonState(button, options)
    button.show()
  }

  setBackButton(options: BackButtonOptions | null): void {
    const button = this.webApp?.BackButton
    if (!button) {
      return
    }

    if (this.backClick) {
      button.offClick(this.backClick)
      this.backClick = null
    }

    if (!options || options.visible === false) {
      button.hide()
      return
    }

    if (options.onClick) {
      this.backClick = options.onClick
      button.onClick(this.backClick)
    }
    button.show()
  }

  haptic(style: HapticStyle = 'light'): void {
    const haptic = this.webApp?.HapticFeedback
    if (!haptic) {
      return
    }
    switch (style) {
      case 'error':
      case 'success':
      case 'warning':
        haptic.notificationOccurred(style)
        break
      case 'selection':
        haptic.selectionChanged()
        break
      case 'light':
      case 'medium':
      case 'heavy':
      case 'rigid':
      case 'soft':
        haptic.impactOccurred(style)
        break
      default: {
        const _exhaustive: never = style
        void _exhaustive
      }
    }
  }

  close(): void {
    this.webApp?.close()
  }

  openLink(url: string): void {
    if (!url) {
      return
    }
    const webApp = this.webApp
    if (webApp?.openLink) {
      webApp.openLink(url)
      return
    }
    openExternalUrl(url)
  }

  requestContact(): Promise<string | null> {
    const webApp = this.webApp
    if (!webApp?.requestContact) {
      return Promise.resolve(null)
    }

    return new Promise((resolve) => {
      let settled = false

      const settle = (phone: string | null) => {
        if (settled) {
          return
        }
        settled = true
        window.clearTimeout(timeoutId)
        webApp.offEvent('contactRequested', onContact)
        resolve(phone)
      }

      const onContact = (...args: unknown[]) => {
        const event = args[0] as {
          status?: string
          responseUnsafe?: { contact?: { phone_number?: string } }
        } | undefined
        if (event?.status === 'sent') {
          settle(event.responseUnsafe?.contact?.phone_number ?? null)
          return
        }
        settle(null)
      }

      const timeoutId = window.setTimeout(() => {
        settle(null)
      }, REQUEST_CONTACT_TIMEOUT_MS)

      webApp.onEvent('contactRequested', onContact)
      webApp.requestContact((shared) => {
        if (!shared) {
          settle(null)
        }
        // shared === true: wait for contactRequested (or timeout above).
      })
    })
  }

  getStartParam(): string | null {
    const fromUnsafe = this.webApp?.initDataUnsafe?.start_param
    if (fromUnsafe) {
      return fromUnsafe
    }
    if (typeof window === 'undefined') {
      return null
    }
    const params = new URLSearchParams(window.location.search)
    return params.get('tgWebAppStartParam') || params.get('startapp') || null
  }

  private clearMainClick(button: TelegramMainButton): void {
    if (this.mainClick) {
      button.offClick(this.mainClick)
      this.mainClick = null
    }
  }

  private applyMainButtonState(
    button: TelegramMainButton,
    options: MainButtonOptions,
  ): void {
    button.setText(options.text)
    if (options.enabled === false) {
      button.disable()
    }
    else {
      button.enable()
    }
    if (options.loading) {
      button.showProgress(false)
    }
    else {
      button.hideProgress()
    }
    if (options.onClick) {
      this.mainClick = options.onClick
      button.onClick(this.mainClick)
    }
  }
}
